import type {
  UpdateMealMarketplaceConfigArgs,
  FetchMealSignedUploadUrlArgs,
  FetchMealByIdArgs,
  UnarchiveMealArgs,
  ArchiveMealArgs,
  CreateMealArgs,
  FetchMealsArgs,
  EditMealArgs,
} from './types/meals.types';
import type { UiSignedUploadUrl } from './types/files.types';
import type { ApiMeal } from '../schemas/meals.schema';

import { createMealApiArgs, mapMealFromApi } from './mappers/meals.mappers';
import { postWithPagination, destroy, patch, post, get } from './axios';
import { DEFAULT_CURRENCY } from '../config/constants';

export { mapMealFromApi };

export const createMeal = async ({
  organizationId,
  ...args
}: CreateMealArgs) => {
  const response = await post<ApiMeal>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/meals`,
    createMealApiArgs(args),
  );
  if (!response?.data) return null;
  return mapMealFromApi(response.data, args.locale, args.userUnitSettings);
};

export const editMeal = async ({
  id,
  organizationId,
  ...args
}: EditMealArgs) => {
  const response = await patch<ApiMeal>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/meals/${id}`,
    createMealApiArgs(args),
  );
  if (!response?.data) return null;
  return mapMealFromApi(response.data, args.locale, args.userUnitSettings);
};

export const fetchMealById = async ({
  id,
  locale,
  organizationId,
  userUnitSettings,
}: FetchMealByIdArgs) => {
  const response = await get<{ data: ApiMeal }>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/meals/${id}`,
  );
  if (!response?.data) return null;
  return mapMealFromApi(response.data, locale, userUnitSettings);
};

export const fetchMeals = async ({
  contentFilters,
  locale,
  organizationId,
  tableFilters,
}: FetchMealsArgs) => {
  const { search, ...restTableFilters } = tableFilters;
  const { authorIds, isDeleted, isHome, isMarketplace, sources, tags } =
    contentFilters ?? {};

  const response = await postWithPagination<any[]>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/meals/list`,
    {
      ...restTableFilters,
      ...(search ? { search } : {}),
      ...(tags ? { tagIds: tags } : {}),
      authorIds,
      isDeleted,
      isHome,
      isMarketplace,
      sources,
    },
  );
  if (!response?.data) return null;
  return {
    ...response,
    data: response.data.map((meal) => mapMealFromApi(meal, locale)),
  };
};

export const fetchMealSignedUploadUrl = async ({
  mediaType,
  organizationId,
  target,
}: FetchMealSignedUploadUrlArgs): Promise<UiSignedUploadUrl> => {
  const response = await get<{ data: UiSignedUploadUrl }>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/meals/signed-upload-url?mediaType=${mediaType}${target ? `&target=${target}` : ''}`,
  );
  return response?.data as UiSignedUploadUrl;
};

export const archiveMeal = async ({ id, organizationId }: ArchiveMealArgs) => {
  await destroy(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/meals/${id}`,
  );
  return true;
};

export const unarchiveMeal = async ({
  id,
  organizationId,
}: UnarchiveMealArgs) => {
  await patch(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/meals/${id}/restore`,
  );
  return true;
};

export const updateMealMarketplaceConfig = async ({
  id,
  currency = DEFAULT_CURRENCY,
  enabled,
  organizationId,
  price,
}: UpdateMealMarketplaceConfigArgs) => {
  await patch(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/meals/${id}`,
    {
      configuration: {
        marketplace: { currency, enabled, price: enabled ? price : 0 },
      },
    },
  );
};
